import type {TFunction} from "i18next";
import type {ColumnBodyOptions} from "primereact/column";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {Message} from "primereact/message";
import {SelectButton} from "primereact/selectbutton";
import {Tooltip} from "primereact/tooltip";
import {useId} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared";
import type {RevaLanguage} from "@reva/shared/i18n";
import {formatPrice} from "@reva/shared/number-format";
import {normalizeSpf, SPF_PARAMS} from "@reva/shared/parameter-resolution";

import {useLanguage} from "../../hooks/languageHooks";
import {selectAppreciationHasStoredValueCurried} from "../../redux/appreciationsUtil";
import {selectHeatingOlderThan30Years} from "../../redux/energySelectors";
import {selectAllInitialValuationParams, selectAllInspectionParams} from "../../redux/objectParametersSlice";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {resetOrEmptySpf, saveSpfComment, selectCanResetSpf} from "../../redux/specialPropertyFeaturesThunk";
import type {RevaDispatch} from "../../redux/store";
import {selectEffectiveIndicationData} from "../../redux/valueIndicationSelectors";
import {ColumnHeaderWithIcons} from "./ColumnHeaderWithIcons";
import {CurrencyInputNumber} from "./CurrencyInputNumber";
import {CellInputActionIcon} from "./InputGrossFloorArea";
import InputTextField from "./InputTextField";
import {useInputParameterDef, useMultipleParameterValues} from "./paramHooks";
import {ROW_ACTION_COLUMN_STYLE} from "./RowDeleteIcon";

const TK = "object.tab_inspection.input";
const SELECTABLE_FEATURES = {pool: "Pool", sauna: "Sauna"} as const;
const REPLACEMENT_COST_NAME_MARKER = "ersatzbeschaffungskosten";

type SpfParam = (typeof SPF_PARAMS)[keyof typeof SPF_PARAMS];
type FeatureKey = keyof typeof SELECTABLE_FEATURES;
type Params = Record<string, unknown> | null | undefined;
type ParamArray = unknown[] | null | undefined;

/** Eine Zeile der Tabelle; `newRow` markiert die leere Eingabezeile. */
interface SpfRow {
    name?: string | null;
    value?: number | null;
    valueLending?: number | null;
    share?: number | null;
    newRow?: boolean;
}

interface SpfWarning {
    text: string;
    add?: FeatureKey;
}

interface IndicatedFeature {
    feature_de?: string | null;
    value: number;
}

interface SpfIndicationData {
    special_property_feature_list?: {
        special_property_feature_list?: IndicatedFeature[];
        special_property_feature_list_lending?: IndicatedFeature[];
    }[];
    special_properties?: number | null;
    special_properties_lending?: number | null;
}

interface ParameterSpec {
    descriptionDe?: string | null;
    descriptionEn?: string | null;
}

interface InputSpecialPropertyFeaturesEditPanelProps {
    disabled?: boolean;
    compact?: boolean;
}

export function InputSpecialPropertyFeaturesEditPanel({disabled, compact}: InputSpecialPropertyFeaturesEditPanelProps) {
    const {parameterValues: spfAll, onValuesChange} = useMultipleParameterValues(
        Object.values(SPF_PARAMS),
        "INSPECTION_DERIVED",
    );
    const spf = normalizeSpf(
        Object.fromEntries(Object.values(SPF_PARAMS).map((p) => [p, spfAll[p]?.activeParameterValue])),
    );
    const column = (param: SpfParam): ParamArray => spf?.[param];
    const spfValues = column(SPF_PARAMS.values);
    const spfNames = column(SPF_PARAMS.names) as (string | null)[] | null | undefined;
    const spfValuesLending = column(SPF_PARAMS.valuesLending);
    const spfShares = column(SPF_PARAMS.shares);
    const spfComment: string | null | undefined = spfAll[SPF_PARAMS.values].comment;
    const inspectionData = useSelector(selectAllInspectionParams) as Params;
    const initialData = useSelector(selectAllInitialValuationParams) as Params;
    const indicationData = useSelector(selectEffectiveIndicationData) as SpfIndicationData | null;
    // #18998: der Warnhinweis gilt nur, solange der Energie-Würdigungstext noch der berechnete Default ist -
    // bei manuell überschriebenem Text (DOC_PROPERTY) hat der Gutachter die Kontrolle über den Bezug zu den BoG.
    const heatingOlderThan30Years = useSelector(selectHeatingOlderThan30Years);
    const hasStoredEnergyAppreciation = useSelector(
        selectAppreciationHasStoredValueCurried("appreciation_object_energy"),
    );
    const dispatch = useDispatch<RevaDispatch>();
    const {t} = useTranslation();
    const language = useLanguage();
    const readOnly = useSelector(selectReadOnly);
    const canReset = useSelector(selectCanResetSpf);
    const isActive = !readOnly && !disabled;

    const spfData: SpfRow[] =
        spfNames?.map((name, index) => ({
            name,
            value: spfValues?.[index] as number | null,
            valueLending: spfValuesLending?.[index] as number | null | undefined,
            share: spfShares?.[index] as number | null | undefined,
        })) || [];
    if (isActive) spfData.push({newRow: true});
    const availableFeatures = Object.values(SELECTABLE_FEATURES).filter((name) => !hasFeature(spfNames, name));

    const formatPlaceholder = (value: number) =>
        `${t("object.associatedOutputParamPrefix")} ${formatPrice(value, language)}`;
    const placeholders = (features: IndicatedFeature[] | undefined): Record<string, string> =>
        Object.fromEntries(
            [...(features ?? [])].reverse().map((item) => [item.feature_de ?? "", formatPlaceholder(item.value)]),
        );
    const indicatedFeatures = indicationData?.special_property_feature_list?.[0];
    const placeholder = placeholders(indicatedFeatures?.special_property_feature_list);
    const placeholderLending = placeholders(indicatedFeatures?.special_property_feature_list_lending);
    const warnings = isActive
        ? getWarnings(
              initialData,
              inspectionData,
              indicationData,
              t,
              language,
              spfNames,
              heatingOlderThan30Years && !hasStoredEnergyAppreciation,
          )
        : [];

    const possibleComment = spfComment != null ? {[SPF_PARAMS.values]: spfComment} : undefined;

    function onChange(
        rowIndex: number,
        value: string | number | null | undefined,
        newRow: SpfRow,
        paramName: SpfParam,
        emptyValue?: string | null,
    ) {
        const oldValues = column(paramName);
        if ((!value && spfData[rowIndex].newRow) || value === oldValues?.[rowIndex]) {
            return;
        } else if (spfData[rowIndex].newRow) {
            addRow(rowIndex, newRow, value);
        } else {
            const adjustedValues = adjustValuesIfEmpty(rowIndex, value, oldValues, emptyValue, spfNames?.length ?? 0);
            const newVal = adjustedValues?.map((v, i) => (i === rowIndex ? value : v)) ?? null;
            const comment = paramName === SPF_PARAMS.names ? undefined : possibleComment;
            const clearValues =
                paramName === SPF_PARAMS.shares
                    ? {
                          [SPF_PARAMS.values]: spfValues?.map((v, i) => (i === rowIndex ? null : v)),
                          [SPF_PARAMS.valuesLending]:
                              spfValuesLending?.map((v, i) => (i === rowIndex ? null : v)) ?? null,
                      }
                    : undefined;
            onValuesChange({[paramName]: newVal, ...clearValues}, comment);
        }
    }

    const addRow = (index: number, data: SpfRow, value: unknown) => {
        if (!value) return;
        onValuesChange(
            {
                [SPF_PARAMS.names]: insertAt(spfNames, index, data.name),
                [SPF_PARAMS.shares]: spfShares || data.share ? insertAt(spfShares, index, data.share) : null,
                [SPF_PARAMS.values]: insertAt(spfValues, index, data.value),
                [SPF_PARAMS.valuesLending]: insertAt(spfValuesLending, index, data.valueLending),
            },
            possibleComment,
        );
    };

    const onRowDelete = (index: number) => {
        const notEmpty = (spfNames?.length ?? 0) > 1;
        const withoutRow = (values: ParamArray, whenEmpty: [] | null) =>
            notEmpty ? values?.filter((_, i) => i !== index) : whenEmpty;
        onValuesChange(
            {
                [SPF_PARAMS.names]: withoutRow(spfNames, []),
                [SPF_PARAMS.shares]: withoutRow(spfShares, null),
                [SPF_PARAMS.values]: withoutRow(spfValues, []),
                [SPF_PARAMS.valuesLending]: withoutRow(spfValuesLending, []),
            },
            possibleComment,
        );
    };

    const onReset = (newValue: null | []) => dispatch(resetOrEmptySpf(newValue));

    const addComment = (comment: string | null) => dispatch(saveSpfComment(comment));

    const namesSpec: ParameterSpec | null = useInputParameterDef(SPF_PARAMS.names);
    const sharesSpec: ParameterSpec | null = useInputParameterDef(SPF_PARAMS.shares);
    const valuesSpec: ParameterSpec | null = useInputParameterDef(SPF_PARAMS.values);
    const valuesLendingSpec: ParameterSpec | null = useInputParameterDef(SPF_PARAMS.valuesLending);

    const desc = (spec: ParameterSpec | null) => selectByLanguage(language, spec?.descriptionDe, spec?.descriptionEn);
    const colHeader = (labelKey: string, spec: ParameterSpec | null) =>
        compact && spfData.length === 0 ? (
            ""
        ) : (
            <SpfColumnHeader label={t(`${TK}.${labelKey}`)} description={desc(spec)} />
        );

    const firstColumnHeader = (
        <ColumnHeaderWithIcons
            onReset={onReset}
            title={<SpfColumnHeader label={t(`${TK}.spf_name`)} description={desc(namesSpec)} />}
            comment={spfComment}
            addComment={addComment}
            showReset={canReset}
            isActive={isActive}
            parameterType="INSPECTION_DERIVED"
            parameter={SPF_PARAMS.values}
            label={t(`${TK}.spf_title`)}
        />
    );

    return (
        <>
            <DataTable
                id={"input_inspection_special_property_features"}
                className="p-inputtext-sm text-sm inputtext-compact w-full"
                value={spfData}
                cellMemo={false}
                pt={{headerRow: {className: "font-bold"}}}
                size="small"
                footer={
                    !compact && (
                        <SumFooter
                            market={indicationData?.special_properties}
                            lending={indicationData?.special_properties_lending}
                            show={spfData.length > 1}
                        />
                    )
                }
            >
                {isActive && (
                    <Column
                        style={ROW_ACTION_COLUMN_STYLE}
                        body={(rowData: SpfRow, {rowIndex}: ColumnBodyOptions) => (
                            <CellInputActionIcon rowData={rowData} onDelete={() => onRowDelete(rowIndex)} />
                        )}
                    />
                )}
                <Column
                    header={compact ? "" : firstColumnHeader}
                    field="name"
                    bodyStyle={compact ? {minWidth: "6rem"} : {}}
                    alignHeader="left"
                    headerClassName="w-full"
                    body={(rowData: SpfRow, {rowIndex}: ColumnBodyOptions) => (
                        <CellInputName
                            rowData={rowData}
                            options={availableFeatures}
                            disabled={!isActive || isSelectableFeature(rowData.name)}
                            onChange={(value) => onChange(rowIndex, value, {name: value}, SPF_PARAMS.names, "")}
                        />
                    )}
                />
                <Column
                    header={colHeader("spf_shares", sharesSpec)}
                    field="share"
                    alignHeader="right"
                    body={(rowData: SpfRow, {rowIndex}: ColumnBodyOptions) => (
                        <CellInputShare
                            rowData={rowData}
                            disabled={!isActive}
                            onChange={(value) => onChange(rowIndex, value, {share: value}, SPF_PARAMS.shares, null)}
                        />
                    )}
                />
                <Column
                    header={colHeader("spf_market", valuesSpec)}
                    field="value"
                    alignHeader="right"
                    body={(rowData: SpfRow, {rowIndex}: ColumnBodyOptions) => (
                        <CellInputValue
                            rowData={rowData}
                            field="value"
                            disabled={!isActive}
                            emptyValue={placeholder}
                            onChange={(value) => onChange(rowIndex, value, {value}, SPF_PARAMS.values)}
                        />
                    )}
                />
                <Column
                    header={colHeader("spf_lending", valuesLendingSpec)}
                    field="valueLending"
                    alignHeader="right"
                    body={(rowData: SpfRow, {rowIndex}: ColumnBodyOptions) => (
                        <CellInputValue
                            rowData={rowData}
                            field="valueLending"
                            disabled={!isActive}
                            emptyValue={placeholderLending}
                            onChange={(value) =>
                                onChange(rowIndex, value, {valueLending: value}, SPF_PARAMS.valuesLending, null)
                            }
                        />
                    )}
                />
            </DataTable>
            {!compact &&
                warnings.map((item) => (
                    <Message
                        severity="warn"
                        key={item.text}
                        className={`w-full ${item.add ? "cursor-pointer" : ""}`}
                        text={item.text}
                        onClick={() =>
                            item.add && addRow(spfData.length - 1, {name: SELECTABLE_FEATURES[item.add]}, true)
                        }
                    />
                ))}
        </>
    );
}

// ein noch fehlendes Array wird mit Leerwerten angelegt, ein nur noch leeres wieder zu null
export function adjustValuesIfEmpty(
    index: number,
    value: unknown,
    oldValues: ParamArray,
    emptyValue: unknown,
    rowCount: number,
): ParamArray {
    if (oldValues === null && rowCount > 0 && value !== emptyValue) {
        return Array(rowCount).fill(emptyValue);
    } else if (oldValues != null && value === emptyValue) {
        const hasNonDefault = oldValues.find((v, i) => v !== emptyValue && v !== null && i !== index) !== undefined;
        return hasNonDefault ? oldValues : null;
    } else {
        return oldValues;
    }
}

// füllt fehlende/zu kurze Arrays auf, sonst landet der Wert vor dem Zeilenindex
export const insertAt = (values: ParamArray, index: number, value: unknown) => {
    const padded = Array.from({length: Math.max(index, values?.length ?? 0)}, (_, i) => values?.[i] ?? null);
    return [...padded.slice(0, index), value, ...padded.slice(index)];
};

const isSelectableFeature = (name: string | null | undefined) =>
    name != null && name.toLowerCase() in SELECTABLE_FEATURES;

const hasFeature = (names: unknown, feature: string) =>
    Array.isArray(names) &&
    names.some((name) => typeof name === "string" && name.toLowerCase() === feature.toLowerCase());

function getWarnings(
    initialData: Params,
    inspectionData: Params,
    indicationData: SpfIndicationData | null,
    t: TFunction,
    language: RevaLanguage,
    spfNames: (string | null)[] | null | undefined,
    expectsReplacementCostDeduction: boolean,
): SpfWarning[] {
    const warnings: SpfWarning[] = [];
    // #18998: der (Default-)Energie-Würdigungstext verweist bei alter Heizung auf einen Abzug hier.
    if (
        expectsReplacementCostDeduction &&
        !spfNames?.some((name) => name?.toLowerCase().includes(REPLACEMENT_COST_NAME_MARKER))
    ) {
        warnings.push({text: t(`${TK}.spf_missing_replacement_cost`)});
    }
    for (const key of ["pool", "sauna"] as const) {
        const feature = SELECTABLE_FEATURES[key];
        if (hasFeature(spfNames, feature)) continue;
        const inInspection = inspectionData?.[`prop_${key}`] || hasFeature(inspectionData?.[SPF_PARAMS.names], feature);
        const source = inInspection ? "inspection" : initialData?.[`prop_${key}`] ? "initialValuation" : null;
        if (source) {
            const args = {feature, source: t(`object.category.${source}`)};
            warnings.push({text: t(`${TK}.spf_missing`, args), add: key});
        }
    }

    const spfInitValue = sum(initialData?.[SPF_PARAMS.values]);
    const spfInitValueLending = sum(initialData?.[SPF_PARAMS.valuesLending]);
    if (spfInitValue != null && Math.abs((indicationData?.special_properties ?? 0) - spfInitValue) > 1000) {
        warnings.push({text: t(`${TK}.spf_difference`, {valueEp: formatPrice(spfInitValue, language)})});
    } else if (
        spfInitValueLending != null &&
        Math.abs((indicationData?.special_properties_lending ?? 0) - spfInitValueLending) > 1000
    ) {
        warnings.push({text: t(`${TK}.spf_difference`, {valueEp: formatPrice(spfInitValueLending, language)})});
    }

    return warnings;
}

const sum = (values: unknown) =>
    Array.isArray(values) ? values.reduce<number>((acc, value) => acc + ((value as number | null) ?? 0), 0) : null;

interface CellInputNameProps {
    rowData: SpfRow;
    options: string[];
    disabled: boolean;
    onChange: (value: string) => void;
}

function CellInputName({rowData, options, disabled, onChange}: CellInputNameProps) {
    const optionsClassName = `py-0 px-4 rounded-l-none ${options.length === 1 ? "rounded-r" : ""}`;
    const pt = {button: () => ({className: optionsClassName})};
    let inputClassName = !rowData.name && options.length > 0 ? "!rounded-r-none" : "";
    inputClassName += !rowData.name && !rowData.newRow ? " p-invalid" : "";

    return (
        <div className="p-inputgroup">
            <InputTextField
                value={rowData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                placeholder="Name"
                disabled={disabled}
                textAlign="left"
                className={inputClassName}
                style={{minWidth: "13rem"}}
            />
            {!rowData.name && (
                <SelectButton className="flex" pt={pt} options={options} onChange={(e) => onChange(e.value)} />
            )}
        </div>
    );
}

interface CellInputValueProps {
    rowData: SpfRow;
    field: "value" | "valueLending";
    disabled: boolean;
    onChange: (value: number | null | undefined) => void;
    emptyValue: Record<string, string>;
}

function CellInputValue({rowData, field, disabled, onChange, emptyValue}: CellInputValueProps) {
    const language = useLanguage();
    const currentValue = rowData[field] ?? null;
    const indicated = rowData.newRow ? undefined : emptyValue[rowData.name ?? ""];
    const invalid = !rowData.newRow && currentValue == null && !indicated && rowData.share == null;

    return (
        <CurrencyInputNumber
            value={currentValue}
            placeholder={indicated ?? "0"}
            inputClassName="w-36"
            locale={language}
            onValueChange={(e) => onChange(e.value)}
            invalid={invalid}
            disabled={disabled}
        />
    );
}

interface CellInputShareProps {
    rowData: SpfRow;
    disabled: boolean;
    onChange: (value: number | null | undefined) => void;
}

function CellInputShare({rowData, disabled, onChange}: CellInputShareProps) {
    const language = useLanguage();
    return (
        <InputNumber
            value={rowData.share ?? null}
            inputClassName="text-right w-28"
            locale={language}
            onValueChange={(e) => onChange(e.value)}
            min={-100}
            max={100}
            disabled={disabled}
            suffix={" %"}
        />
    );
}

interface SumFooterProps {
    market: number | null | undefined;
    lending: number | null | undefined;
    show: boolean;
}

function SumFooter({market, lending, show}: SumFooterProps) {
    const language = useLanguage();
    return !show ? null : (
        <div className="font-bold w-full flex flex-row items-center inputtext-compact">
            <span className="text-right flex-1">&Sigma;</span>
            <span className="text-right w-40">{formatPrice(market ?? 0, language)}</span>
            <span className="text-right mr-1 w-40">{formatPrice(lending ?? 0, language)}</span>
        </div>
    );
}

function SpfColumnHeader({label, description}: {label: string; description: string | null | undefined}) {
    const id = useId().replaceAll(":", "-");
    return (
        <>
            <span id={id}>{label}</span>
            {description && (
                <Tooltip target={`#${id}`} position="top" className="custom-tooltip">
                    {description}
                </Tooltip>
            )}
        </>
    );
}
